import { useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  Upload, FileText, CheckCircle2, ArrowRight, AlertCircle,
  Sparkles, Info, ChevronRight
} from "lucide-react";

const extractedConditions = [
  { num: "1.1", title: "Construction Management Plan", stage: "Pre-Start", confidence: 97 },
  { num: "1.2", title: "Sediment and erosion controls", stage: "Pre-Start", confidence: 95 },
  { num: "1.3", title: "TPZ fencing as per arborist", stage: "Pre-Start", confidence: 98 },
  { num: "2.1", title: "Drainage certificate from engineer", stage: "Pre-Slab", confidence: 94 },
  { num: "2.2", title: "Soil report verification", stage: "Pre-Slab", confidence: 92 },
  { num: "2.3", title: "Stormwater connection approval", stage: "Pre-Slab", confidence: 89 },
  { num: "2.4", title: "Setback confirmation survey", stage: "Pre-Slab", confidence: 96 },
  { num: "3.1", title: "Acoustic compliance report", stage: "Pre-Frame", confidence: 91 },
  { num: "3.2", title: "Wind assessment for upper levels", stage: "Pre-Frame", confidence: 87 },
  { num: "4.1", title: "Landscape plan completion", stage: "Pre-OC", confidence: 93 },
  { num: "4.2", title: "Car park line marking and signage", stage: "Pre-OC", confidence: 96 },
  { num: "4.3", title: "External lighting compliance", stage: "Pre-OC", confidence: 88 },
  { num: "4.4", title: "Waste management plan implemented", stage: "Pre-OC", confidence: 90 },
  { num: "4.5", title: "Accessibility compliance certificate", stage: "Pre-OC", confidence: 94 },
  { num: "5.1", title: "Sediment control maintenance", stage: "Ongoing", confidence: 97 },
  { num: "5.2", title: "Amenity and noise compliance", stage: "Ongoing", confidence: 86 },
  { num: "5.3", title: "TPZ fencing maintained during works", stage: "Ongoing", confidence: 95 },
  { num: "5.4", title: "Hours of construction compliance", stage: "Ongoing", confidence: 98 },
];

export default function DecisionImport() {
  const [step, setStep] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted] = useState(false);
  const [published, setPublished] = useState(false);

  const handleUpload = () => {
    setUploading(true);
    setTimeout(() => {
      setUploading(false);
      setStep(1);
      setExtracting(true);
      setTimeout(() => {
        setExtracting(false);
        setExtracted(true);
      }, 2500);
    }, 1500);
  };

  const handlePublish = () => {
    setPublished(true);
    setStep(2);
  };

  const stages = ["Upload decision", "Review extracted conditions", "Publish to team"];

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="decision-import-page">
        {/* Page explainers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card className="border-blue-100 bg-blue-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-blue-700 mb-0.5">What this page does</p>
                <p className="text-xs text-blue-600">Upload a decision letter PDF. AI extracts conditions and groups them by build stage for your team.</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 bg-slate-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <ArrowRight className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-0.5">What happens next</p>
                <p className="text-xs text-slate-500">Review extracted conditions, then publish to your team. Conditions flow into Stage Conditions for tracking.</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 bg-slate-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <Upload className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-0.5">Who uses this</p>
                <p className="text-xs text-slate-500">Builder PM, permit admin. Anyone receiving a decision letter from council.</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Wizard steps */}
        <div className="flex items-center gap-2 mb-2">
          {stages.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                step >= i ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-400"
              }`}>
                {step > i ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
              </div>
              <span className={`text-sm font-medium ${step >= i ? "text-slate-900" : "text-slate-400"}`}>{s}</span>
              {i < 2 && <ChevronRight className="w-4 h-4 text-slate-300 mx-1" />}
            </div>
          ))}
        </div>

        {/* Step 0: Upload */}
        {step === 0 && (
          <Card className="border-slate-200" data-testid="upload-step">
            <CardContent className="py-12 px-8 text-center">
              <div className="max-w-md mx-auto">
                <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                  <Upload className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="font-heading font-semibold text-xl text-slate-900 mb-2">Upload a decision letter</h3>
                <p className="text-sm text-slate-500 mb-6">
                  Upload the PDF of the decision letter or notice of decision. AI will extract conditions and group them by stage.
                </p>
                <div
                  className="border-2 border-dashed border-slate-300 rounded-xl p-8 hover:border-blue-300 hover:bg-blue-50/30 transition-colors cursor-pointer mb-6"
                  onClick={handleUpload}
                  data-testid="upload-dropzone"
                >
                  {uploading ? (
                    <div className="space-y-3">
                      <p className="text-sm text-slate-600">Uploading Decision_DCC_2024_0456.pdf...</p>
                      <Progress value={75} className="h-2" />
                    </div>
                  ) : (
                    <>
                      <FileText className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                      <p className="text-sm text-slate-600 font-medium">Drop PDF here or click to browse</p>
                      <p className="text-xs text-slate-400 mt-1">Supports decision letters, notices of decision, and permit conditions</p>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 1: Review extracted */}
        {step === 1 && (
          <div className="space-y-4">
            {extracting ? (
              <Card className="border-slate-200" data-testid="extracting-state">
                <CardContent className="py-16 text-center">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mx-auto mb-4 animate-pulse">
                    <Sparkles className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">Extracting conditions...</h3>
                  <p className="text-sm text-slate-500 mb-4">AI is reading the decision letter and identifying conditions.</p>
                  <Progress value={60} className="h-2 max-w-xs mx-auto" />
                </CardContent>
              </Card>
            ) : extracted ? (
              <>
                {/* Metadata */}
                <Card className="border-slate-200" data-testid="extraction-metadata">
                  <CardContent className="py-4 px-5">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Council</p>
                        <p className="text-sm font-medium text-slate-900">Darebin City Council</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Permit Number</p>
                        <p className="text-sm font-medium text-slate-900">DCC-2024-0456</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Address</p>
                        <p className="text-sm font-medium text-slate-900">88 High St, Preston VIC</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Date</p>
                        <p className="text-sm font-medium text-slate-900">13/01/2025</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Summary */}
                <div className="flex items-center gap-4">
                  <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-sm py-1 px-3">
                    {extractedConditions.length} conditions found
                  </Badge>
                  <span className="text-xs text-slate-400">
                    Average extraction confidence: {Math.round(extractedConditions.reduce((a, c) => a + c.confidence, 0) / extractedConditions.length)}%
                  </span>
                </div>

                {/* Human in the loop box */}
                <Card className="border-amber-200 bg-amber-50/30" data-testid="human-in-loop-notice">
                  <CardContent className="py-3 px-4 flex items-start gap-3">
                    <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Nothing is auto-approved</p>
                      <p className="text-xs text-amber-600">Review all extracted conditions before publishing to your team. AI extraction may miss or misinterpret items.</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Side by side: PDF preview + extracted rows */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* PDF preview */}
                  <Card className="border-slate-200" data-testid="pdf-preview">
                    <CardContent className="py-4 px-4">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Decision Letter Preview</p>
                      <div className="bg-slate-50 rounded-lg p-6 space-y-4 text-sm text-slate-600 leading-relaxed border border-slate-200">
                        <p className="font-medium text-slate-800">NOTICE OF DECISION TO GRANT A PERMIT</p>
                        <p>Application No: DCC-2024-0456</p>
                        <p>The responsible authority has decided to grant a permit subject to the following conditions:</p>
                        <div className="space-y-2 text-xs">
                          <p><strong>1.</strong> Before the development starts, a Construction Management Plan must be submitted to and approved by the Responsible Authority.</p>
                          <p><strong>2.</strong> Before the development starts, sediment and erosion control measures must be installed on the site.</p>
                          <p><strong>3.</strong> Before the development starts, tree protection zone fencing must be installed as per the approved arborist report.</p>
                          <p className="text-slate-400 italic">... 15 more conditions</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Extracted conditions */}
                  <Card className="border-slate-200" data-testid="extracted-conditions">
                    <CardContent className="py-4 px-4">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Extracted Conditions</p>
                      <div className="space-y-1.5 max-h-[400px] overflow-y-auto pr-1">
                        {extractedConditions.map((c) => (
                          <div key={c.num} className="flex items-center gap-3 bg-slate-50 rounded-lg p-2.5 hover:bg-slate-100 transition-colors">
                            <span className="font-mono text-xs text-slate-400 w-8 shrink-0">{c.num}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-slate-900 truncate">{c.title}</p>
                              <p className="text-[10px] text-slate-400">{c.stage}</p>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              <div className={`w-1.5 h-1.5 rounded-full ${c.confidence >= 90 ? "bg-emerald-500" : "bg-amber-500"}`} />
                              <span className="text-[10px] text-slate-400">{c.confidence}%</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Publish button */}
                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => { setStep(0); setExtracted(false); }}>
                    Start over
                  </Button>
                  <Button
                    className="bg-blue-600 text-white hover:bg-blue-700 gap-2"
                    onClick={handlePublish}
                    data-testid="publish-conditions-btn"
                  >
                    <CheckCircle2 className="w-4 h-4" /> Publish {extractedConditions.length} conditions to team
                  </Button>
                </div>
              </>
            ) : null}
          </div>
        )}

        {/* Step 2: Published */}
        {step === 2 && published && (
          <Card className="border-emerald-200 bg-emerald-50/30" data-testid="published-state">
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="font-heading font-semibold text-xl text-slate-900 mb-2">{extractedConditions.length} conditions published</h3>
              <p className="text-sm text-slate-500 mb-6">
                Conditions have been added to Stage Conditions for 88 High St Mixed Use.
              </p>
              <div className="flex justify-center gap-3">
                <Button className="bg-slate-900 text-white hover:bg-slate-800 gap-2" onClick={() => window.location.href = "/builder/stage-conditions"} data-testid="go-to-conditions-btn">
                  View in Stage Conditions <ArrowRight className="w-4 h-4" />
                </Button>
                <Button variant="outline" onClick={() => { setStep(0); setExtracted(false); setPublished(false); }} data-testid="import-another-btn">
                  Import another decision
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info footer */}
        <div className="flex items-start gap-2">
          <Info className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
          <p className="text-[11px] text-slate-400">
            AI extraction is a starting point. Always verify conditions against the source document. easypermit does not guarantee extraction accuracy.
          </p>
        </div>
      </div>
    </AppLayout>
  );
}
